import { Component, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api-services/api.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CitySuggestion, UnleashFeatureFlags, WeatherData } from 'src/app/interfaces/interfaces';
import { LanguageService } from 'src/app/services/languageservice/language.service';
import { UserStore } from 'src/app/stores/user.store';
import { GeoLocationService, LocationData, AddressData } from 'src/app/services/geo-location.service';
import { UnleashService } from 'src/app/services/unleash/unleash.service';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-weather-widget',
  templateUrl: './weather-widget.component.html',
  styleUrls: ['./weather-widget.component.scss'],
})
export class WeatherWidgetComponent implements OnInit {
  // Weather data and display
  weatherData: WeatherData;
  city: string = this.userStore.userPreferences().userSettings.weatherLocation || '';
  isLoading = false;

  // Modal state
  isModalOpen = false;
  tempCity: CitySuggestion = { name: '', country: '', region: '' };
  citySuggestions: CitySuggestion[] = [];
  searchSubject = new Subject<string>();

  // Location-based weather
  useMyLocation = this.userStore.userPreferences().userSettings.useLocationForWeather || false;
  currentLocation: LocationData | null = null;
  locationError: string | null = null;
  isGettingLocation = false;

  // Feature flags
  unleashFeatureFlags = UnleashFeatureFlags;

  constructor(
    private apiService: ApiService,
    public unleashService: UnleashService,
    private languageService: LanguageService,
    private userStore: UserStore,
    private geoLocationService: GeoLocationService,
    private alertController: AlertController,
    private translateService: TranslateService
  ) {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getCitySuggestions(searchTerm);
      });
  }

  ngOnInit() {
    this.initializeWeatherState();
  }

  private initializeWeatherState() {
      if (this.useMyLocation) {
        this.getWeatherByLocation();
      } else if (this.city) {
        this.getWeatherByCity(this.city);
      }
  }

  private getWeatherByCity(city: string) {
    if (city) {
      this.isLoading = true;
      this.city = city;
      this.languageService.selectedLanguage$.subscribe((language) => {
        this.apiService.getWeatherByCity(this.city, language.value).subscribe({
          next: (data) => {
            this.weatherData = data;
            this.isLoading = false;
          },
          error: () => {
            this.weatherData = null;
            this.isLoading = false;
          },
        });
      });
    }
  }

  private async getWeatherByLocation() {
    try {
      this.isLoading = true;
      this.isGettingLocation = true;
      this.locationError = null;

      const permissionResult = await this.geoLocationService.checkLocationPermission();
      
      if (permissionResult.status === 'granted') {
        await this.fetchWeatherFromLocation();
      } else if (permissionResult.status === 'denied') {
        const shouldOpenSettings = await this.showLocationSettingsAlert();
        if (shouldOpenSettings) {
          await this.geoLocationService.openLocationSettings();
        }
        this.locationError = permissionResult.error || 'widgets.location.permissionDenied';
        this.useMyLocation = false;
        this.isLoading = false;
      } else {
        // Request permission
        const requestResult = await this.geoLocationService.requestLocationPermission();
        if (requestResult.status === 'granted') {
          await this.fetchWeatherFromLocation();
        } else {
          const shouldOpenSettings = await this.showLocationSettingsAlert();
          if (shouldOpenSettings) {
            await this.geoLocationService.openLocationSettings();
          }
          this.locationError = requestResult.error || 'widgets.location.permissionHelp';
          this.useMyLocation = false;
          this.isLoading = false;
        }
      }
    } catch (error) {
      this.locationError = 'widgets.location.permissionHelp';
      this.useMyLocation = false;
      this.isLoading = false;
    } finally {
      this.isGettingLocation = false;
    }
  }

  private async fetchWeatherFromLocation() {
    try {
      // Get current location
      const locationResult = await this.geoLocationService.getCurrentLocation();
      
      if (locationResult.location) {
        this.currentLocation = locationResult.location;
        
        // Reverse geocode to get city name
        const addressResult = await this.geoLocationService.reverseGeocode(locationResult.location);
        
        if (addressResult.address && addressResult.address.city) {
          this.city = addressResult.address.city;
          
          // Fetch weather for the detected city
          this.languageService.selectedLanguage$.subscribe((language) => {
            this.apiService.getWeatherByCity(this.city, language.value).subscribe({
              next: (data) => {
                this.weatherData = data;
                this.isLoading = false;
              },
              error: () => {
                this.weatherData = null;
                this.isLoading = false;
              },
            });
          });
        } else {
          this.locationError = addressResult.error || 'widgets.location.permissionHelp';
          this.useMyLocation = false;
          this.isLoading = false;
        }
      } else {
        this.locationError = locationResult.error || 'widgets.location.permissionHelp';
        this.useMyLocation = false;
        this.isLoading = false;
      }
    } catch (error) {
      this.locationError = 'widgets.location.permissionHelp';
      this.useMyLocation = false;
      this.isLoading = false;
    }
  }

  onSearchInput(event: any) {
    this.tempCity = { name: '', country: '', region: '' };
    const value = event.target.value?.trim();
    if (value) {
      this.isLoading = true;
      this.searchSubject.next(value);
    } else {
      this.citySuggestions = [];
    }
  }

  private getCitySuggestions(searchTerm: string) {
    this.apiService.getCitySuggestions(searchTerm).subscribe({
      next: (suggestions) => {
        this.citySuggestions = suggestions;
        this.isLoading = false;
      },
      error: () => {
        this.citySuggestions = [];
        this.isLoading = false;
      },
    });
  }

  selectSuggestion(city: CitySuggestion) {
    this.tempCity = city;
  }

  // Modal methods
  openModal() {
    this.isModalOpen = true;
    this.initializeModalState();
  }

  private initializeModalState() {
    const userPreferences = this.userStore.userPreferences();
    if (userPreferences?.userSettings) {
      this.useMyLocation = userPreferences.userSettings.useLocationForWeather || false;
      if (this.city) {
        this.tempCity = { name: this.city, country: '', region: '' };
      }
    }
  }

  confirmCity() {
    if (this.tempCity && this.tempCity.name) {
      this.city = this.tempCity.name;
      const currentPreferences = this.userStore.userPreferences();
      this.userStore
        .updateUserSettings({
          ...currentPreferences?.userSettings,
          weatherLocation: this.tempCity.name,
          useLocationForWeather: this.useMyLocation,
        })
        .then(() => {
          this.getWeatherByCity(this.city);
          this.isModalOpen = false;
          this.tempCity = { name: '', country: '', region: '' };
          this.citySuggestions = [];
          this.isLoading = false;
        });
    }
  }

  async toggleUseMyLocation() {
    if (this.useMyLocation) {
      await this.getCurrentLocationForCity();
    } else {
      this.currentLocation = null;
      this.locationError = null;
    }
    
    this.saveLocationPreference();
  }

  private saveLocationPreference() {
    const currentPreferences = this.userStore.userPreferences();
    this.userStore
      .updateUserSettings({
        ...currentPreferences?.userSettings,
        useLocationForWeather: this.useMyLocation,
      })
      .catch((error) => {
        console.error('Error saving location preference:', error);
      });
  }

  async getCurrentLocationForCity() {
    try {
      this.isGettingLocation = true;
      this.locationError = null;

      const permissionResult = await this.geoLocationService.checkLocationPermission();
      
      if (permissionResult.status === 'granted') {
        await this.fetchCityFromLocation();
      } else if (permissionResult.status === 'denied') {
        const shouldOpenSettings = await this.showLocationSettingsAlert();
        if (shouldOpenSettings) {
          await this.geoLocationService.openLocationSettings();
        }
        this.locationError = permissionResult.error || 'widgets.location.permissionHelp';
        this.useMyLocation = false;
      } else {
        // Request permission
        const requestResult = await this.geoLocationService.requestLocationPermission();
        if (requestResult.status === 'granted') {
          await this.fetchCityFromLocation();
        } else {
          const shouldOpenSettings = await this.showLocationSettingsAlert();
          if (shouldOpenSettings) {
            await this.geoLocationService.openLocationSettings();
          }
          this.locationError = requestResult.error || 'widgets.location.permissionHelp';
          this.useMyLocation = false;
        }
      }
    } catch (error) {
      this.locationError = 'widgets.location.permissionHelp';
      this.useMyLocation = false;
    } finally {
      this.isGettingLocation = false;
    }
  }

  private async fetchCityFromLocation() {
    try {
      // Get current location
      const locationResult = await this.geoLocationService.getCurrentLocation();
      
      if (locationResult.location) {
        this.currentLocation = locationResult.location;
        
        // Reverse geocode to get city name
        const addressResult = await this.geoLocationService.reverseGeocode(locationResult.location);
        
        if (addressResult.address && addressResult.address.city) {
          this.tempCity = {
            name: addressResult.address.city,
            country: addressResult.address.country || '',
            region: addressResult.address.state || ''
          };
        } else {
          this.locationError = addressResult.error || 'widgets.location.permissionHelp';
          this.useMyLocation = false;
        }
      } else {
        this.locationError = locationResult.error || 'widgets.location.permissionHelp';
        this.useMyLocation = false;
      }
    } catch (error) {
      this.locationError = 'widgets.location.permissionHelp';
      this.useMyLocation = false;
    }
  }

  async getCurrentLocationAndWeather() {
    await this.getWeatherByLocation();
  }

  async retryLocationDetection() {
    this.locationError = null;
    await this.getCurrentLocationForCity();
  }

  private async showLocationSettingsAlert(): Promise<boolean> {
    const alert = await this.alertController.create({
      header: this.translateService.instant('widgets.weather.locationSettingsAlertTitle'),
      message: this.translateService.instant('widgets.weather.locationSettingsAlertMessage'),
      mode: 'ios',
      cssClass: 'custom-alert',
      buttons: [
        {
          text: this.translateService.instant('widgets.weather.locationSettingsOpen'),
          role: 'confirm',
          cssClass: 'text-confirm',
        },
        {
          text: this.translateService.instant('widgets.weather.locationSettingsCancel'),
          role: 'cancel',
           cssClass: 'text-confirm',
        },
      ],
    });

    await alert.present();
    const { role } = await alert.onDidDismiss();

    return role === 'confirm';
  }
}
